import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Hammer, ChevronDown } from 'lucide-react';
import { services } from '../data';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Forside', href: '/', isLink: true },
    { name: 'Om mig', href: '/om-os', isLink: true },
    { name: 'Kontakt', href: '/kontakt', isLink: true },
  ];

  const textColor = 'text-slate-900';
  const logoColor = 'text-slate-900';

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 bg-white ${scrolled ? 'shadow-md py-2' : 'shadow-sm py-[5px]'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img 
                src="https://assets.auxodev.dk/assets/66803e0c-9723-4cfe-96f2-7ea52c9dabed/ac21f12e-bf64-4bfd-8c41-734aaf4e0241.jpg" 
                alt="Nielsen Brolægning Logo" 
                className={`transition-all duration-300 object-contain ${scrolled ? 'h-10 md:h-12' : 'h-20 md:h-24'}`}
              />
            </Link>
          </div>
          
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-8">
              <Link 
                to="/" 
                className={`text-sm font-medium transition-colors hover:text-orange-500 ${textColor}`}
              >
                Forside
              </Link>
              
              {/* Dropdown Menu */}
              <div 
                className="relative group"
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <button
                  className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-orange-500 ${textColor}`}
                >
                  Ydelser <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                <div className={`absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 transition-all duration-200 ${dropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                  <div className="py-1">
                    {services.map((service) => (
                      <Link
                        key={service.slug}
                        to={`/ydelser/${service.slug}`}
                        className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-orange-600 border-b border-slate-50 last:border-0"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {navLinks.slice(1).map((link) => (
                link.isLink ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`text-sm font-medium transition-colors hover:text-orange-500 ${textColor}`}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className={`text-sm font-medium transition-colors hover:text-orange-500 ${textColor}`}
                  >
                    {link.name}
                  </a>
                )
              ))}
              <Link
                to="/kontakt"
                className="bg-orange-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-orange-700 transition-colors shadow-lg shadow-orange-600/20"
              >
                Kontakt
              </Link>
            </div>
          </div>

          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-md ${textColor}`}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-b border-slate-100 overflow-y-auto max-h-[calc(100vh-80px)]">
          <div className="flex flex-col">
            <Link
              to="/"
              className={`block px-6 py-5 text-base font-medium border-b-[0.5px] border-slate-100 transition-colors ${
                isActive('/') ? 'text-orange-600' : 'text-slate-700 hover:text-orange-600'
              }`}
              onClick={() => setIsOpen(false)}
            >
              Forside
            </Link>
            
            <div className="bg-slate-50/50 py-2 border-b-[0.5px] border-slate-100">
              <p className="px-6 text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-1">Ydelser</p>
              {services.map((service) => (
                <Link
                  key={service.slug}
                  to={`/ydelser/${service.slug}`}
                  className={`block px-8 py-3 text-base font-medium border-b-[0.5px] border-slate-100/50 last:border-0 transition-colors ${
                    isActive(`/ydelser/${service.slug}`) ? 'text-orange-600' : 'text-slate-600 hover:text-orange-600'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {service.title}
                </Link>
              ))}
            </div>

            {navLinks.slice(1).map((link) => (
              link.isLink ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`block px-6 py-5 text-base font-medium border-b-[0.5px] border-slate-100 transition-colors ${
                    isActive(link.href) ? 'text-orange-600' : 'text-slate-700 hover:text-orange-600'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  className="block px-6 py-5 text-base font-medium border-b-[0.5px] border-slate-100 text-slate-700 hover:text-orange-600 transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </a>
              )
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
